"use client";

import { curveLinear } from "@visx/curve";
import { scaleLinear, scaleTime } from "@visx/scale";
import { AreaClosed, LinePath } from "@visx/shape";
import { extent, max, min } from "d3-array";
import React, { useMemo } from "react";
import type { BaseDataPoint } from "@/utils/charts";
import type { CellComponentProps } from "../DataTable.types";

export interface SparklineCellProps extends CellComponentProps {
  data?: BaseDataPoint[]; // Optional for direct usage
  type?: "line" | "area" | "both";
  color?: string;
  strokeWidth?: number;
  categoryColors?: Record<string, string>;
  categoryLabels?: Record<string, string>;
  width?: number;
  height?: number;
}

/**
 * SparklineCell
 *
 * Mini line and/or area chart for time series previews in table cells.
 * Uses the same BaseDataPoint[] data structure as full LineSeries/AreaSeries components.
 * Supports category-based coloring for conditional visualization.
 */
export const SparklineCell = React.memo<SparklineCellProps>(
  ({
    value,
    data: dataProp,
    type = "line",
    color = "var(--color-viz-default)",
    strokeWidth = 2,
    categoryColors,
    context,
    width,
    height = 40,
  }) => {
    // Use value from DataTable accessor, fall back to data prop
    const data = (value as BaseDataPoint[]) || dataProp || [];

    // Determine actual width (default to 100px if not specified)
    const chartWidth = width || 100;
    const chartHeight = height;

    // Create scales
    const { xScale, yScale } = useMemo(() => {
      if (data.length === 0) {
        return {
          xScale: scaleTime({
            range: [0, chartWidth],
            domain: [new Date(), new Date()],
          }),
          yScale: scaleLinear({ range: [chartHeight, 0], domain: [0, 1] }),
        };
      }

      const dateExtent = extent(data, (d) => d.xValue) as [Date, Date];
      const yMin = min(data, (d) => d.yValue) || 0;
      const yMax = max(data, (d) => d.yValue) || 0;

      // Add small padding to y-scale
      const yPadding = (yMax - yMin) * 0.1 || 1;

      return {
        xScale: scaleTime({
          range: [0, chartWidth],
          domain: dateExtent,
        }),
        yScale: scaleLinear({
          range: [chartHeight, 0],
          domain: [yMin - yPadding, yMax + yPadding],
        }),
      };
    }, [data, chartWidth, chartHeight]);

    // Loading state
    if (context.isLoading) {
      return (
        <div className="flex items-center" style={{ width: chartWidth, height: chartHeight }}>
          <div className="h-1 w-full animate-pulse bg-border-default rounded" />
        </div>
      );
    }

    // Empty state
    if (!data || data.length === 0) {
      return (
        <div
          className="flex items-center justify-center text-text-secondary text-xs"
          style={{ width: chartWidth, height: chartHeight }}
        >
          No data
        </div>
      );
    }

    // Determine the actual color to use
    // If category colors are provided, use the first data point's category
    const effectiveColor =
      categoryColors && data[0]?.category && categoryColors[data[0].category]
        ? categoryColors[data[0].category]
        : color;

    return (
      <svg width={chartWidth} height={chartHeight} className="overflow-visible">
        {(type === "area" || type === "both") && (
          <AreaClosed
            data={data}
            x={(d) => xScale(d.xValue)}
            y={(d) => yScale(d.yValue)}
            yScale={yScale}
            fill={effectiveColor}
            fillOpacity={0.15}
            curve={curveLinear}
          />
        )}

        {(type === "line" || type === "both") && (
          <LinePath
            data={data}
            x={(d) => xScale(d.xValue)}
            y={(d) => yScale(d.yValue)}
            stroke={effectiveColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            curve={curveLinear}
          />
        )}
      </svg>
    );
  }
);

SparklineCell.displayName = "SparklineCell";
